import { useState } from 'react';
import Link from 'next/link';
import { cx } from '@/utils';
import SvgArrow from '@common/Icons/Arrow';
import SvgHouse from '@common/Icons/House';
import { t } from '@lingui/macro';

import styles from './styles.module.css';

export type BreadcrumbItem = { name: string; route: string; icon?: React.ReactNode };

//Interface ICommonBlockProps es usado en los bloques para saber la ruta previa y el breadcrumb
export interface INavigationBlockProps {
  defaultBreadcrumb?: BreadcrumbItem[];
  prevRoute?: string;
}
//-------------------------------------------------------------
export type BreadcrumbsProps = {
  color?: string;
  colorLink?: string;
  items: BreadcrumbItem[];
  initRoute?: string;
};

const Breadcrumbs = ({
  color = 'text-white',
  colorLink = 'text-white',
  items,
  initRoute = '/',
}: BreadcrumbsProps) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Renderizar tooltip con items intermedios
  const renderTooltip = (hiddenItems: { name: string; route: string }[]) => {
    if (!showTooltip) return null;

    return (
      <div className="absolute left-0 top-full z-50 mt-1 min-w-[200px] max-w-[400px] rounded-md border border-gray-200 bg-white shadow-lg">
        <div className="py-2">
          {hiddenItems.map((item, index) => (
            <Link
              key={index}
              href={item.route}
              className="block px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-100"
              onClick={() => setShowTooltip(false)}
            >
              <div className="truncate" title={item.name}>
                {item.name}
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={cx(styles.wrapper, color)}>
      <div className={'flex items-baseline gap-[4px]'}>
        <SvgHouse className={'text-primary'} />
        <Link href={initRoute} className={cx(styles.text, colorLink)}>
          {'Inicio'}
        </Link>
      </div>

      {items.map((item, key) => {
        // Si hay más de 7 items, mostrar truncado
        if (items.length > 7) {
          // Mostrar los primeros 3 items
          if (key < 3) {
            return (
              <div key={key} className={'flex items-baseline gap-[10px]'}>
                <div className="text-silver">/</div>
                <Link
                  href={item.route}
                  className={cx(
                    styles.text,
                    'inline-block max-w-[200px] overflow-hidden overflow-ellipsis whitespace-nowrap text-primary',
                    colorLink
                  )}
                  style={{ color: colorLink }}
                  title={item.name}
                >
                  {item.name}
                </Link>
              </div>
            );
          }
          // Mostrar "..." con tooltip después del tercer item
          else if (key === 3) {
            const hiddenItems = items.slice(3, items.length - 1);

            return (
              <div key={key} className={'relative flex items-baseline gap-[10px]'}>
                <div className="text-silver">/</div>
                <div
                  className={cx(
                    styles.text,
                    'cursor-pointer select-none transition-colors hover:text-primary'
                  )}
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  title="Haga clic para ver más elementos"
                >
                  ...
                </div>
                {renderTooltip(hiddenItems)}
              </div>
            );
          }
          // Mostrar solo el último item
          else if (key === items.length - 1) {
            return (
              <div key={key} className={'flex items-baseline gap-[10px]'}>
                <div className="text-silver">/</div>
                <div
                  className={cx(
                    styles.text,
                    'inline-block max-w-[300px] overflow-hidden overflow-ellipsis whitespace-nowrap'
                  )}
                  title={item.name}
                >
                  {item.name}
                </div>
              </div>
            );
          }
          // Ocultar items intermedios
          else {
            return null;
          }
        }
        // Si hay 7 items o menos, mostrar todos normalmente
        else {
          return (
            <div key={key} className={'flex items-baseline gap-[10px]'}>
              <div className="text-silver">/</div>
              {items.length - 1 === key ? (
                <div
                  className={cx(
                    styles.text,
                    'inline-block max-w-[300px] overflow-hidden overflow-ellipsis whitespace-nowrap'
                  )}
                  title={item.name}
                >
                  {item.icon ? <span className="mr-2 inline-block">{item.icon}</span> : null}
                  {item.name}
                </div>
              ) : (
                <Link
                  href={item.route}
                  className={cx(
                    styles.text,
                    'inline-block max-w-[300px] overflow-hidden overflow-ellipsis whitespace-nowrap text-primary',
                    colorLink
                  )}
                  style={{ color: colorLink }}
                  title={item.name}
                >
                  {item.icon ? <span className="mr-2 inline-block">{item.icon}</span> : null}
                  {item.name}
                </Link>
              )}
            </div>
          );
        }
      })}
    </div>
  );
};

export default Breadcrumbs;
